import store from "@/store";
import { InspectKbn, SeibiKbn } from "@/model/InspectKbn";
import { InspectStaff } from "@/model/InspectStaff";
import { LiftUse } from "@/model/LiftUse";
import { InspectDetail } from "@/model/InspectDetail";
import { InspectionDetailState } from "@/store/modules/inspectionDetailStore";
import { IppanSeibiService } from "@/service/IppanSeibiService";

const namespace = "InspectionDetail";

export class InspectionInspectLineDetailStoreService {
  private ippanSeibiService: IppanSeibiService;

  // サービス初期化時に検査ラインの選択管理枠をStoreへ登録する
  constructor() {
    this.ippanSeibiService = new IppanSeibiService();
    this.addNowSelected();
  }

  // InspectionDetail Store全体の現在状態を参照する
  get currentState(): InspectionDetailState {
    return store.getters[`${namespace}/currentState`];
  }

  // 検査ラインが選択済みかどうかを参照する
  get nowSelected(): boolean {
    const nowSelected = store.getters[`${namespace}/getNowSelected`];
    if (nowSelected.inspectLine === true || nowSelected.inspectLine === false) {
      return nowSelected.inspectLine;
    }
    this.addNowSelected();
    return false;
  }

  // 検査ライン一覧の編集用データを参照する
  get inspectLine(): InspectKbn[] {
    return this.currentState.inspectLine;
  }

  // 検査ライン一覧の選択済みデータを参照する
  get selectedInspectLine(): InspectKbn[] {
    return store.getters[`${namespace}/getInspectLine`];
  }

  // 検査ラインを使用する設定かどうかを参照する
  get useLine(): boolean {
    return store.getters[`${namespace}/useLine`];
  }

  // 検査構成画面の入力を減らす設定かどうかを参照する
  get isInspectionLessInput(): boolean | null {
    return store.getters["FunctionSetting/isInspectionLessInput"];
  }

  // 検査ライン一覧をStoreへ反映する
  setInspectLine(payload: InspectKbn[]) {
    return store.dispatch(`${namespace}/setInspectLine`, payload);
  }

  // setLiftUse(payload: LiftUse[]) {
  //   return store.dispatch(`${namespace}/setLiftUse`, payload);
  // }

  // setShakenShurui(payload: SeibiKbn[]) {
  //   return store.dispatch(`${namespace}/setShakenShurui`, payload);
  // }

  // 検査ラインの選択管理枠をStoreへ追加する
  addNowSelected() {
    return store.dispatch(`${namespace}/addNowSelected`, "inspectLine");
  }

  // 検査ラインの選択済み状態をStoreへ反映する
  setNowSelected(payload: boolean) {
    return store.dispatch(`${namespace}/setNowSelected`, {
      key: "inspectLine",
      value: payload
    });
  }

  // 検査ラインの選択状態からスタート可否判定用の状態を更新する
  checkDisableStart(arrData: any, isUnSelect?: boolean) {
    let checked: boolean = false;
    if (isUnSelect) {
      checked = true;
    } else if (arrData) {
      checked = arrData.some((data: any) => data.selected == "1");
    }
    this.setNowSelected(checked);
  }

  // 保存対象の検査ラインコードをStoreへ反映する
  setInspectLineCd(payload: string | number) {
    return store.dispatch(`${namespace}/setInspectLineCd`, payload);
  }

  // 共通処理から扱う検査ライン一覧を返す
  get list(): InspectKbn[] {
    return this.inspectLine;
  }

  // 共通処理から検査ライン一覧をStoreへ反映する
  setList(payload: InspectKbn[]) {
    return this.setInspectLine(payload);
  }

  // 共通処理から検査ラインコードをStoreへ反映する
  setCd(payload: string | number) {
    return this.setInspectLineCd(payload);
  }

  // テンプレート表示用の検査ライン選択肢リストを生成する
  getTargetList() {
    return this.list.map((data: any) => {
      return {
        "mode": 1,
        "btnDel": false,
        "btnSel": data.selected == '1',
        "btnFre": data.selected == '0',
        "btnUse": false,
        "key": data.kbnKey,
        "dataSet": data.kbnKey,
        "isDisabled": data.isDisabled == 1,
        "icon": "icon-line",
        "label": data.value,
        "labelEntends1": null,
        "labelEntends2": null,
        "labelEntends3": null,
      };
    });
  }

  // スタート時に選択中の検査ラインを保存データへ反映する
  setStartInspectDetail(inspectionDetail: InspectDetail) {
    const selectedData = this.list.find((data: InspectKbn) => {
      return data.selected == "1";
    });
    if (!selectedData) { return; }

    inspectionDetail.inspectLineCd = String(selectedData.kbnKey);
    inspectionDetail.inspectLineNm = selectedData.value;
    this.setCd(selectedData.kbnKey);
  }

  // 取得済みの検査構成から検査ラインの初期選択情報を反映する
  applyInitialInspectionDetail(
    data: any,
    inspectionDetail: InspectDetail
  ) {
    if (this.ippanSeibiService.isIppanSeibi || this.isInspectionLessInput) {
      // 一般整備または「車検の検査構成画面の入力を減らす」がONの場合は、検査ライン「判定なし」固定
      inspectionDetail.inspectLineCd = "3";
    }
    // 将来的にこの記述も'3'にした方が良いと思われる（変更もれなのかどうか未検証）
    this.setCd(data.inspectLineCd);
  }

  // 検査ラインの選択クリックを処理する
  select(kbnKey: number) {
    const inspectLine = this.list;
    inspectLine.map(data => {
      if (data.kbnKey == kbnKey) {
        if (data.selected == "1") {
          data.selected = "0";
        } else {
          data.selected = "1";
        }
      } else {
        data.selected = "0";
      }
    });
    this.setList([...inspectLine]);
    this.checkDisableStart(inspectLine);
  }

  // 検査ラインで「選択無し」を表示できるか返す
  canDeSelect() {
    return false;
  }

  // 検査ラインが「選択無し」状態か返す
  isDeSelected() {
    return null;
  }

  // 共通呼び出し用の「選択無し」クリック処理
  clickDeSelect() {
    // ダミー
    return false;
  }

  // Storeに既存リストがある場合に選択状態を再評価する
  applyExistingList() {
    const inspectLine = this.list;
    this.setList(inspectLine);
    this.checkDisableStart(inspectLine);
    return inspectLine;
  }

  // API取得結果から検査ライン一覧の初期選択状態を作成する
  setInitialList(res: InspectKbn[], inspectLineCd: string | number | undefined) {
    const inspectLine = res.map(data => {
      if (inspectLineCd && +inspectLineCd == data.kbnKey) {
        data.selected = "1";
        this.setNowSelected(true);
      } else {
        data.selected = "0";
      }
      if (this.ippanSeibiService.isIppanSeibi || this.isInspectionLessInput) {
        // 一般整備または「車検の検査構成画面の入力を減らす」がONの場合は、検査ライン「判定なし」固定
        data.isDisabled = 1;
      }
      return data;
    });
    this.setList(inspectLine);
    return inspectLine;
  }

  // 検査ライン一覧を既存StoreまたはAPIから取得する
  getDataList(
    loadInitialList: () => Promise<InspectKbn[]>,
    inspectLineCd: string | number | undefined
  ) {
    if (this.list.length > 0) {
      this.applyExistingList();
      return {
        hasExistingList: true,
        promise: null
      };
    }
    return {
      hasExistingList: false,
      promise: loadInitialList().then((res: InspectKbn[]) => {
        return this.setInitialList(res, inspectLineCd);
      })
    };
  }
}
